/**
 * Documents Contract — The typed API contract for the Documents domain.
 *
 * WHY THIS EXISTS:
 * This file defines EXACTLY what the UI expects from the backend for documents.
 * Both mock and HTTP adapters must return data conforming to these types.
 * When FastAPI endpoints go live, they MUST match these shapes — or the adapter
 * translates at the boundary.
 *
 * MIGRATION NOTE:
 * Zod schemas are re-exported from auto-generated code (@hey-api/openapi-ts).
 * Types inferred from generated schemas. UI-only constructs remain hand-written.
 */

import { z } from 'zod';
import type { PaginatedResponse, PaginationParams, SortParams, ApiResponse } from './shared';

// Re-export generated schemas with the names this module has always exported
import {
  zDocumentFormat as GeneratedDocumentFormat,
  zDocumentStatus as GeneratedDocumentStatus,
  zPipelineStage as GeneratedPipelineStage,
  zStageStatus as GeneratedStageStatus,
  zPipelineStageState as GeneratedPipelineStageState,
  zDocumentOwner as GeneratedDocumentOwner,
  zEnrichmentSignal as GeneratedEnrichmentSignal,
  zDocumentEnrichment as GeneratedDocumentEnrichment,
  zArtifactCounts as GeneratedArtifactCounts,
  zArtifactStatusEnum as GeneratedArtifactStatusEnum,
  zPerArtifactStatusRead as GeneratedPerArtifactStatusRead,
  zDocumentDetailRead as GeneratedDocumentDetailRead,
} from '@/api/generated/hey-api/zod.gen';

// ─── Enums ─────────────────────────────────────────────────────────────

export const DocumentFormatEnum = GeneratedDocumentFormat;
export type DocumentFormat = z.infer<typeof DocumentFormatEnum>;

export const DocumentStatusEnum = GeneratedDocumentStatus;
export type DocumentStatus = z.infer<typeof DocumentStatusEnum>;

// ─── Frontend logical pipeline stage (5-stage abstraction shown in table inline view) ─
export const PipelineStageEnum = GeneratedPipelineStage;
export type PipelineStage = z.infer<typeof PipelineStageEnum>;

// ─── Backend 16-stage pipeline enum ────────────────────────────────────
// UI-only: The generated code does not expose a separate backend pipeline stage enum.
// The IngestionStepDetail uses raw strings for stage. Keep hand-written for frontend mapping.
export const BackendPipelineStageEnum = z.enum([
  'preflight',
  'dedup',
  'parse',
  'metadata',
  'thumbnail',
  'segment',
  'detect',
  'extract',
  'structure',
  'citations',
  'claims',
  'embed',
  'enrich',
  'summarize',
  'qa_precompute',
  'ready',
]);
export type BackendPipelineStage = z.infer<typeof BackendPipelineStageEnum>;

export const StageStatusEnum = GeneratedStageStatus;
export type StageStatus = z.infer<typeof StageStatusEnum>;

// ─── Backend job step status ────────────────────────────────────────────
// UI-only: The generated code uses raw strings in IngestionStepDetail.
// Keep hand-written for type-safe matching in pipeline UI.
export const JobStepStatusEnum = z.enum(['pending', 'running', 'success', 'failed', 'skipped']);
export type JobStepStatus = z.infer<typeof JobStepStatusEnum>;

// ─── Artifact types ─────────────────────────────────────────────────────
// UI-only: No generated ArtifactType enum exists; the generated EvidenceArtifactRead
// uses a raw string for type. Keep hand-written for frontend artifact filtering.
export const ArtifactTypeEnum = z.enum([
  'section',
  'table',
  'figure',
  'citation',
  'claim',
  'cross_reference',
  'equation',
]);
export type ArtifactType = z.infer<typeof ArtifactTypeEnum>;

// ─── Artifact content schemas (typed per artifact_type) ─────────────────
// UI-only: Content schemas are frontend-specific typed-content discriminants.
// The generated code represents content as a generic record.

// Shared AI description fields — populated by artifact_descriptions service (Increment 5)
const AiDescriptionFields = {
  ai_brief: z.string().nullable().optional(),
  ai_description: z.string().nullable().optional(),
  ai_why_it_matters: z.string().nullable().optional(),
};

export const SectionContentSchema = z.object({
  heading: z.string(),
  text: z.string(),
  level: z.number().int().nullable().optional(),
  ...AiDescriptionFields,
});
export type SectionContent = z.infer<typeof SectionContentSchema>;

export const TableContentSchema = z.object({
  headers: z.array(z.string()),
  rows: z.array(z.array(z.string())),
  caption: z.string().nullable().optional(),
  natural_language: z.string().nullable().optional(),
  ...AiDescriptionFields,
});
export type TableContent = z.infer<typeof TableContentSchema>;

export const FigureContentSchema = z.object({
  caption: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  ...AiDescriptionFields,
});
export type FigureContent = z.infer<typeof FigureContentSchema>;

export const CitationContextTypeEnum = z.enum(['supporting', 'contrasting', 'mentioning']);
export type CitationContextType = z.infer<typeof CitationContextTypeEnum>;

export const CitationContextSchema = z.object({
  contextType: CitationContextTypeEnum,
  contextSentence: z.string().nullable().optional(),
  contextConfidence: z.number().min(0).max(1).nullable().optional(),
});
export type CitationContext = z.infer<typeof CitationContextSchema>;

export const CitationContentSchema = z.object({
  doi: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  authors: z.array(z.string()).optional().default([]),
  year: z.number().int().nullable().optional(),
  journal: z.string().nullable().optional(),
  rawText: z.string().nullable().optional(),
  crossrefVerified: z.boolean().optional(),
  matchScore: z.number().nullable().optional(),
  citationContext: CitationContextSchema.nullable().optional(),
  ...AiDescriptionFields,
});
export type CitationContent = z.infer<typeof CitationContentSchema>;

export const EquationContentSchema = z.object({
  latex: z.string(),
  label: z.string().nullable().optional(),
  renderedText: z.string().nullable().optional(),
  displayMode: z.enum(['block', 'inline']).optional().default('block'),
});
export type EquationContent = z.infer<typeof EquationContentSchema>;

export const EvidenceTypeEnum = z.enum([
  'statistical',
  'observational',
  'causal',
  'comparative',
  'qualitative',
]);
export type EvidenceType = z.infer<typeof EvidenceTypeEnum>;

export const EffectDirectionEnum = z.enum(['positive', 'negative', 'neutral', 'mixed']);
export type EffectDirection = z.infer<typeof EffectDirectionEnum>;

export const ClaimContentSchema = z.object({
  text: z.string(),
  statisticalAnchor: z.string().nullable().optional(),
  context: z.string().nullable().optional(),
  llmConfidence: z.number().min(0).max(1).nullable().optional(),
  evidence_type: EvidenceTypeEnum.nullable().optional(),
  variables: z.array(z.string()).optional(),
  effect_direction: EffectDirectionEnum.nullable().optional(),
  population: z.string().nullable().optional(),
  sample_size: z.number().int().nullable().optional(),
  p_value: z.number().nullable().optional(),
  effect_size: z.number().nullable().optional(),
  confidence_qualifier: z.string().nullable().optional(),
  ...AiDescriptionFields,
});
export type ClaimContent = z.infer<typeof ClaimContentSchema>;

// ─── Artifact (unified read type) ──────────────────────────────────────
// UI-only: The frontend uses a simplified artifact shape with generic content
// for the artifact list/detail views. The generated EvidenceArtifactRead has
// a different shape (image-oriented). Keep hand-written.
export const NormBBoxSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  w: z.number().min(0).max(1),
  h: z.number().min(0).max(1),
});
export type NormBBox = z.infer<typeof NormBBoxSchema>;

export const ArtifactReadSchema = z.object({
  id: z.string().uuid(),
  artifactType: ArtifactTypeEnum,
  page: z.number().int().nullable().optional(),
  position: z.number().int().nullable().optional(),
  content: z.record(z.string(), z.unknown()),
  imagePath: z.string().nullable().optional(),
  previewUrl: z.string().nullable().optional(),
  confidence: z.number().min(0).max(1).nullable().optional(),
  confidenceVersion: z.string().nullable().optional(),
  signals: z.record(z.string(), z.unknown()),
  bboxNorm: NormBBoxSchema.nullable().optional(),
  artifactStatus: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  caption: z.string().nullable().optional(),
  captionOverride: z.string().nullable().optional(),
  ocrText: z.string().nullable().optional(),
  processedPlanTier: z.string().nullable().optional(),
  isPinned: z.boolean().optional().default(false),
  isHidden: z.boolean().optional().default(false),
  createdAt: z.string(),
});
export type ArtifactRead = z.infer<typeof ArtifactReadSchema>;

export interface ArtifactListResponse {
  documentId: string;
  globalVersionId?: string;
  total: number;
  artifacts: ArtifactRead[];
  page?: number;
  pageSize?: number;
  hasMore?: boolean;
}

// ─── Ingestion job step ─────────────────────────────────────────────────
// UI-only: The generated IngestionStepDetail uses snake_case and raw strings.
// Keep hand-written for frontend pipeline status display.
export const IngestionJobStepReadSchema = z.object({
  id: z.string().uuid(),
  stage: BackendPipelineStageEnum,
  status: JobStepStatusEnum,
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  durationMs: z.number().int().nullable().optional(),
  adapterUsed: z.string().nullable().optional(),
  failureReason: z.string().nullable().optional(),
  failureCode: z.string().nullable().optional(),
  retryCount: z.number().int(),
});
export type IngestionJobStepRead = z.infer<typeof IngestionJobStepReadSchema>;

// ─── Ingestion job ──────────────────────────────────────────────────────
// UI-only: The generated IngestionJobDetail uses snake_case fields.
// Keep hand-written for frontend camelCase consistency.
export const IngestionJobReadSchema = z.object({
  id: z.string().uuid(),
  workspaceDocumentId: z.string().uuid(),
  attempt: z.number().int(),
  status: z.string(),
  trigger: z.string(),
  costEstimateUsd: z.number().nullable().optional(),
  traceId: z.string().nullable().optional(),
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  steps: z.array(IngestionJobStepReadSchema),
  createdAt: z.string(),
});
export type IngestionJobRead = z.infer<typeof IngestionJobReadSchema>;

// ─── Per-artifact processing status ────────────────────────────────────
export const ArtifactStatusEnum = GeneratedArtifactStatusEnum;
export type ArtifactStatus = z.infer<typeof ArtifactStatusEnum>;

export const PerArtifactStatusReadSchema = GeneratedPerArtifactStatusRead;
export type PerArtifactStatusRead = z.infer<typeof PerArtifactStatusReadSchema>;

// ─── Processing provenance (from GlobalDocumentVersion) ────────────────
// Mirrors zProcessingProvenanceRead in the generated schema. Backend trims this
// to user-safe trust signals; legacy fields (pipelineVersion, extractorVersion,
// embeddingModel, modelVersions, processedPlanTier) are kept optional here for
// transitional read-tolerance and never rendered in the UI.
export const ProcessingProvenanceReadSchema = z.object({
  parserVendor: z.string(),
  parserVersion: z.string(),
  ocrDetected: z.boolean().optional().default(false),
  parseDegraded: z.boolean().optional().default(false),
  processedAt: z.string().nullable().optional(),
  // Backend-only fields kept optional for transitional decoding only.
  pipelineVersion: z.string().optional(),
  extractorVersion: z.string().optional(),
  embeddingModel: z.string().optional(),
  modelVersions: z.record(z.string(), z.unknown()).nullable().optional(),
  processedPlanTier: z.string().nullable().optional(),
});
export type ProcessingProvenanceRead = z.infer<typeof ProcessingProvenanceReadSchema>;

// ─── Pipeline status (GET /documents/{id}/pipeline) ────────────────────
// UI-only: Composite schema combining ingestion job + artifact statuses.
// The generated code does not expose a combined pipeline status response.
export const PipelineStatusReadSchema = z.object({
  documentId: z.string().uuid(),
  documentStatus: z.string(),
  currentJob: IngestionJobReadSchema.nullable().optional(),
  jobHistory: z.array(IngestionJobReadSchema),
  artifactStatuses: PerArtifactStatusReadSchema.nullable().optional(),
  provenance: ProcessingProvenanceReadSchema.nullable().optional(),
});
export type PipelineStatusRead = z.infer<typeof PipelineStatusReadSchema>;

// ─── DTOs (Data Transfer Objects) ──────────────────────────────────────

/**
 * Pipeline stage status — nested within DocumentDTO.
 */
export const PipelineStageStateSchema = GeneratedPipelineStageState;
export type PipelineStageState = z.infer<typeof PipelineStageStateSchema>;

/**
 * Document owner — lightweight user reference.
 */
export const DocumentOwnerSchema = GeneratedDocumentOwner;
export type DocumentOwner = z.infer<typeof DocumentOwnerSchema>;

/**
 * Enrichment signal — a single scoring dimension from relevance analysis.
 */
export const EnrichmentSignalSchema = GeneratedEnrichmentSignal;
export type EnrichmentSignal = z.infer<typeof EnrichmentSignalSchema>;

/**
 * Document enrichment — workspace-local context computed by ContextEnrichmentService.
 */
export const DocumentEnrichmentSchema = GeneratedDocumentEnrichment;
export type DocumentEnrichment = z.infer<typeof DocumentEnrichmentSchema>;

/**
 * Artifact counts — per-type totals from pipeline extraction.
 */
export const ArtifactCountsSchema = GeneratedArtifactCounts;
export type ArtifactCounts = z.infer<typeof ArtifactCountsSchema>;

/**
 * Document DTO — the canonical shape returned by GET /documents/:id
 * and as items in GET /documents (list).
 */
export const DocumentDTOSchema = GeneratedDocumentDetailRead;
export type DocumentDTO = z.infer<typeof DocumentDTOSchema>;

// ─── Request Types ─────────────────────────────────────────────────────
// UI-only: Request interfaces for frontend query/mutation composition.

/**
 * Filters for listing documents. Sent as query params.
 */
export interface DocumentListFilters {
  status?: DocumentStatus[];
  format?: DocumentFormat[];
  tags?: string[];
  ownerId?: string;
  isKeySource?: boolean;
  dateFrom?: string; // ISO 8601
  dateTo?: string; // ISO 8601
  search?: string; // Full-text search
}

/**
 * Sort fields available for document listing.
 */
export type DocumentSortField = 'title' | 'createdAt' | 'updatedAt' | 'fileSize' | 'status';

/**
 * Complete query params for GET /documents.
 * workspaceId is required — backend enforces tenant isolation.
 */
export interface DocumentListParams
  extends PaginationParams, SortParams<DocumentSortField>, DocumentListFilters {
  /** Workspace UUID — required by backend for tenant isolation. */
  workspaceId: string;
  /** Optional collection to filter by. */
  collectionId?: string;
}

/**
 * Request body for POST /documents (create — metadata only, no file).
 */
export interface CreateDocumentRequest {
  workspaceId: string;
  title: string;
  authors: string[];
  source: string;
  format: DocumentFormat;
  fileName: string;
  fileSize: number;
  pageCount?: number;
  collectionId?: string;
  tags?: string[];
  isKeySource?: boolean;
  notes?: string;
}

/**
 * Request body for PATCH /documents/:id (update).
 */
export interface UpdateDocumentRequest {
  title?: string;
  authors?: string[];
  tags?: string[];
  isKeySource?: boolean;
  notes?: string;
}

// ─── Upload Flow Types ─────────────────────────────────────────────────
// UI-only: Upload flow request/response interfaces for the 3-phase presigned PUT flow.

/**
 * Request body for POST /documents/upload-url (phase 1 of presigned upload).
 */
export interface RequestUploadUrlRequest {
  /** Title shown in the library before processing. */
  title: string;
  /** Original filename (e.g. "attention.pdf"). */
  fileName: string;
  /** File size in bytes — used to validate against backend limits. */
  fileSize: number;
  /** MIME type (e.g. "application/pdf"). Must be in backend allowlist. */
  mimeType: string;
  /** Workspace to place the document in. */
  workspaceId: string;
  collectionId?: string;
  authors?: string[];
  tags?: string[];
  isKeySource?: boolean;
  notes?: string;
  /** Client-computed SHA-256 hex digest (64 chars) for pre-upload dedup. */
  contentHash?: string;
  /**
   * Client-generated idempotency key (16-64 chars, alphanumeric + _ -).
   * Prevents duplicate documents when the client retries on network timeout.
   * Recommended: crypto.randomUUID(). TTL: 5 minutes server-side.
   */
  idempotencyKey?: string;
  /**
   * Pipeline depth: 'full' runs all AI stages, 'fast' parses + indexes
   * but skips extraction, summarization, and Q&A precompute.
   */
  processingMode?: 'full' | 'fast';
}

/**
 * Response from POST /documents/upload-url.
 *
 * Step-by-step:
 *   1. PUT the file blob to uploadUrl with Content-Type = mimeType.
 *   2. Call confirmUpload(documentId, workspaceId).
 */
export interface RequestUploadUrlResponse {
  /** Paper ID — needed for the confirm-upload call. */
  documentId: string;
  /** Opaque storage key (for debugging / idempotency). */
  storageKey: string;
  /** Presigned Supabase Storage PUT URL — expires in expiresInSeconds. */
  uploadUrl: string;
  /** Seconds until the PUT URL expires (typically 300 = 5 min). */
  expiresInSeconds: number;
  /** Headers the client MUST include in the PUT request (Content-Type, Cache-Control, etc.). */
  uploadHeaders?: Record<string, string>;
  /** True when pre-upload dedup matched — skip upload & confirm phases. */
  instantDuplicate?: boolean;
}

/**
 * Request body for POST /documents/{id}/attach-pdf.
 * Generates a presigned URL for attaching a PDF to a metadata-only document.
 */
export interface AttachPdfRequest {
  fileName: string;
  fileSize: number;
  mimeType: string;
  contentHash?: string | null;
}

/**
 * Request body for POST /documents/{id}/confirm-upload (phase 2).
 */
export interface ConfirmUploadRequest {
  /** Optional — client-side PDF page count (from PDF.js or similar). */
  pageCount?: number;
}

// ─── Response Types ────────────────────────────────────────────────────
// UI-only: Response type aliases for frontend convenience.

/** GET /documents → paginated list */
export type DocumentListResponse = PaginatedResponse<DocumentDTO>;

/** GET /documents/:id → single document */
export type DocumentDetailResponse = ApiResponse<DocumentDTO>;

/** POST /documents → created document */
export type CreateDocumentResponse = ApiResponse<DocumentDTO>;

/** PATCH /documents/:id → updated document */
export type UpdateDocumentResponse = ApiResponse<DocumentDTO>;

/** DELETE /documents/:id → confirmation */
export type DeleteDocumentResponse = ApiResponse<{
  id: string;
  deleted: boolean;
  permanent: boolean;
}>;

/** POST /documents/upload-url → presigned PUT URL */
export type RequestUploadUrlApiResponse = ApiResponse<RequestUploadUrlResponse>;

/** POST /documents/{id}/confirm-upload → updated document */
export type ConfirmUploadApiResponse = ApiResponse<DocumentDTO>;

/** GET /documents/download/{id} → presigned download URL */
export interface DownloadUrlResponse {
  url: string;
  fileName: string;
}

// ─── Versioning Types ──────────────────────────────────────────────────
// UI-only: Versioning request/response interfaces for frontend use.

/**
 * Version owner — lightweight user reference.
 */
export interface VersionOwner {
  id: string;
  name: string;
  avatarUrl?: string | null;
}

/**
 * A single document version snapshot.
 * Returned by GET /documents/:id/versions.
 */
export interface DocumentVersionDTO {
  id: string;
  versionNumber: number;
  uploadedBy: VersionOwner;
  fileSize: number;
  timestamp: string; // ISO 8601
  note?: string | null;
}

/**
 * Request body for POST /documents/:id/versions.
 */
export interface CreateVersionRequest {
  note?: string;
}

/**
 * Request body for POST /documents/:id/versions/upload-url.
 * Industry-standard version upload: file metadata + optional note.
 */
export interface RequestVersionUploadUrlRequest {
  /** Original filename (e.g. "attention-v2.pdf"). */
  fileName: string;
  /** File size in bytes. */
  fileSize: number;
  /** MIME type — must be in backend allowlist. */
  mimeType: string;
  /** Optional version note (e.g. "Updated figures"). */
  note?: string;
}

/**
 * Response from POST /documents/:id/versions/upload-url.
 * Includes presigned URL and the snapshot version number.
 */
export interface RequestVersionUploadUrlResponse {
  documentId: string;
  /** Snapshotted version number (previous state). */
  versionNumber: number;
  storageKey: string;
  /** Presigned PUT URL — expires in expiresInSeconds. */
  uploadUrl: string;
  expiresInSeconds: number;
  /** Headers the client MUST include in the PUT request (Content-Type, Cache-Control, etc.). */
  uploadHeaders?: Record<string, string>;
}

/** GET /documents/:id/versions */
export type DocumentVersionsResponse = DocumentVersionDTO[];

// ─── Comment Types ─────────────────────────────────────────────────────
// UI-only: Comment request/response interfaces for frontend use.

/**
 * Comment author — lightweight user reference.
 */
export interface CommentAuthor {
  id: string;
  name: string;
  avatarUrl?: string | null;
  email?: string;
}

/**
 * A single document comment.
 * Returned by GET /documents/:id/comments.
 */
export interface DocumentCommentDTO {
  id: string;
  author: CommentAuthor;
  content: string;
  createdAt: string; // ISO 8601
  editedAt?: string | null;
}

/**
 * Request body for POST /documents/:id/comments.
 */
export interface CreateCommentRequest {
  content: string;
}

/**
 * Request body for PATCH /documents/:id/comments/:commentId.
 */
export interface UpdateCommentRequest {
  content: string;
}

/** GET /documents/:id/comments — paginated envelope. */
export interface DocumentCommentsResponse {
  items: DocumentCommentDTO[];
  total: number;
  nextCursor?: string | null;
}

/** POST /documents/:id/comments */
export type CreateCommentResponse = DocumentCommentDTO;

/** DELETE /documents/:id/comments/:commentId */
export type DeleteCommentResponse = ApiResponse<{ id: string; deleted: boolean }>;

// ─── Adapter Interface ─────────────────────────────────────────────────
// UI-only: Adapter interface for dependency inversion (mock/HTTP).

/**
 * The contract that EVERY documents adapter must implement.
 * Mock adapter and HTTP adapter both conform to this interface.
 * The service layer calls ONLY these methods — never raw fetch.
 */
export interface IDocumentsAdapter {
  /** Fetch a paginated, filtered list of documents. */
  list(params: DocumentListParams): Promise<DocumentListResponse>;

  /** Fetch a single document by ID. */
  getById(id: string, workspaceId: string): Promise<DocumentDetailResponse>;

  /** Create a document metadata record (no file upload). */
  create(data: CreateDocumentRequest): Promise<CreateDocumentResponse>;

  /** Update document metadata. */
  update(
    id: string,
    workspaceId: string,
    data: UpdateDocumentRequest,
  ): Promise<UpdateDocumentResponse>;

  /** Soft-delete a document. */
  delete(id: string, workspaceId: string): Promise<DeleteDocumentResponse>;

  // ── Upload flow ──────────────────────────────────────────────────────

  /**
   * Phase 1: Request a presigned PUT URL for direct-to-storage upload.
   * Creates the Paper record and returns (documentId, uploadUrl).
   */
  requestUploadUrl(data: RequestUploadUrlRequest): Promise<RequestUploadUrlResponse>;

  /**
   * Phase 2: Confirm that the file was successfully uploaded.
   * Advances ingestion_status → STORED and starts the processing pipeline.
   */
  confirmUpload(
    documentId: string,
    workspaceId: string,
    data: ConfirmUploadRequest,
  ): Promise<DocumentDTO>;

  /**
   * Upload a file blob directly to a presigned Supabase PUT URL.
   * This is a raw HTTP PUT — no API server involvement.
   *
   * @param uploadUrl      The presigned URL from requestUploadUrl.
   * @param file           The File object from the browser.
   * @param onProgress     Optional progress callback (0–100).
   * @param uploadHeaders  Headers that MUST be sent with the PUT (from backend response).
   */
  uploadFileToStorage(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void,
    uploadHeaders?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<void>;

  // ── Attach PDF to metadata-only document ─────────────────────────

  /**
   * Request a presigned PUT URL for attaching a PDF to a metadata-only document.
   * Returns the same shape as requestUploadUrl. After upload, call confirmUpload().
   */
  attachPdf(
    documentId: string,
    workspaceId: string,
    data: AttachPdfRequest,
  ): Promise<RequestUploadUrlResponse>;

  // ── Versioning ───────────────────────────────────────────────────

  /** Fetch all versions of a document (newest first). */
  listVersions(documentId: string, workspaceId: string): Promise<DocumentVersionsResponse>;

  /** Create a new version snapshot of a document. */
  createVersion(
    documentId: string,
    workspaceId: string,
    data: CreateVersionRequest,
  ): Promise<DocumentVersionDTO>;

  /**
   * Request a presigned upload URL for a new version of an existing document.
   * Industry-standard flow: snapshots current state → returns upload URL.
   */
  requestVersionUploadUrl(
    documentId: string,
    workspaceId: string,
    data: RequestVersionUploadUrlRequest,
  ): Promise<RequestVersionUploadUrlResponse>;

  // ── Comments ─────────────────────────────────────────────────────

  /** Fetch comments on a document (oldest first, cursor-paginated). */
  listComments(
    documentId: string,
    workspaceId: string,
    options?: { cursor?: string; limit?: number },
  ): Promise<DocumentCommentsResponse>;

  /** Add a comment to a document. */
  addComment(
    documentId: string,
    workspaceId: string,
    data: CreateCommentRequest,
  ): Promise<DocumentCommentDTO>;

  /** Edit a comment. Only the author can edit. */
  editComment(
    documentId: string,
    commentId: string,
    workspaceId: string,
    data: UpdateCommentRequest,
  ): Promise<DocumentCommentDTO>;

  /** Delete a comment. Only the author can delete. */
  deleteComment(
    documentId: string,
    commentId: string,
    workspaceId: string,
  ): Promise<DeleteCommentResponse>;

  // ── Reprocess ─────────────────────────────────────────────────────────

  /**
   * Retry processing for a document stuck in FAILED or STORED state.
   *
   * - quarantine file → re-runs virus scan (SCANNING)
   * - production file → re-runs ingestion pipeline (STORED)
   *
   * Throws 409 if the document is INFECTED (file deleted — re-upload required),
   * already READY, or has an in-flight job running.
   */
  reprocess(documentId: string, workspaceId: string): Promise<DocumentDTO>;

  // ── Document Intelligence Pipeline ────────────────────────────────────

  /**
   * GET /documents/{id}/artifacts
   * Returns all extracted artifacts (sections, tables, figures, citations, claims)
   * for a document that has completed the intelligence pipeline.
   */
  getArtifacts(documentId: string, workspaceId: string): Promise<ArtifactListResponse>;

  /**
   * GET /documents/{id}/pipeline
   * Returns the current ingestion job + full job history with per-stage steps.
   * Poll this when status is processing to drive real-time pipeline UI.
   */
  getPipelineStatus(documentId: string, workspaceId: string): Promise<PipelineStatusRead>;

  /**
   * POST /documents/{id}/extract
   * Enqueues artifact extraction for a document already in READY/READY_WITH_WARNINGS state.
   * Triggers Layer 3 (tables/figures) extraction on demand.
   */
  triggerExtract(documentId: string, workspaceId: string): Promise<void>;

  // ── Download ────────────────────────────────────────────────────────────

  /**
   * GET /documents/download/{id}
   * Returns a presigned GET URL for downloading the document file.
   * URL is Redis-cached (45 min) and proactively refreshed.
   */
  download(documentId: string, workspaceId: string): Promise<DownloadUrlResponse>;

  // ── Move ────────────────────────────────────────────────────────────────

  /**
   * POST /documents/{id}/move?collection_id=...
   * Move a document to a different collection. Pass null to remove from collection.
   */
  move(documentId: string, workspaceId: string, collectionId: string | null): Promise<void>;

  // ── Import ────────────────────────────────────────────────────────────

  /** @deprecated Use previewImport + confirmImport */
  importByIdentifier(identifier: string): Promise<DocumentImportResult>;

  /** POST /documents/import/preview — resolve metadata + check PDF availability. */
  previewImport(identifier: string): Promise<ImportPreviewResult>;

  /** POST /documents/import/confirm — create document with chosen mode. */
  confirmImport(
    identifier: string,
    mode: 'with_pdf' | 'metadata_only',
    processingMode?: 'full' | 'fast',
  ): Promise<ImportConfirmResult>;
}

// ─── Import Types ─────────────────────────────────────────────────────

export interface DocumentImportResult {
  id: string;
  title: string;
  doi?: string | null;
  arxivId?: string | null;
  source: string;
  message: string;
}

export interface ImportPreviewMetadata {
  title?: string | null;
  authors: string[];
  journal?: string | null;
  year?: number | null;
  abstract?: string | null;
  doi?: string | null;
  arxivId?: string | null;
  source: string;
}

export interface ImportPreviewResult {
  metadata: ImportPreviewMetadata;
  pdfAvailable: boolean;
  pdfSource?: string | null;
  identifierType: string;
  identifierValue: string;
}

export interface ImportConfirmResult {
  id: string;
  title: string;
  status: string;
  doi?: string | null;
  arxivId?: string | null;
  source: string;
  message: string;
}

// ─── AI Summary (Multi-Level) ────────────────────────────────────────────────

export type StudyDesign =
  | 'randomized_controlled_trial'
  | 'cohort'
  | 'case_control'
  | 'cross_sectional'
  | 'meta_analysis'
  | 'review'
  | 'qualitative'
  | 'other';

export type BiasRisk = 'low' | 'moderate' | 'high' | 'unclear';

export type SampleSizeAdequacy = 'adequate' | 'borderline' | 'inadequate' | 'not_reported';

export interface StructuredSummary {
  researchQuestion: string | null;
  methodology: string | null;
  keyFindings: string[];
  limitations: string[];
  implications: string | null;
}

export interface DocumentSummary {
  tldr: string | null;
  executive: string | null;
  structured: StructuredSummary | null;
  summaryVersion: string | null;
  model: string | null;
  generatedAt: string | null;
}

export interface MethodologyScore {
  studyDesign: StudyDesign;
  sampleSizeAdequacy: SampleSizeAdequacy;
  statisticalRigor: number;
  biasRisk: BiasRisk;
  reproducibility: number;
  overallQuality: number;
  strengths: string[];
  weaknesses: string[];
}

export interface QAPair {
  question: string;
  answer: string;
  sourceSection: string | null;
  confidence: number;
}

export interface DocumentAISummaryResponse {
  summary: DocumentSummary | null;
  methodologyScore: MethodologyScore | null;
  qaPairs: QAPair[];
}
